import { useEffect, useState, type ReactNode } from "react";
import { Lucide } from "@react-native-vector-icons/lucide";
import { usePathname, useRouter } from "expo-router";
import {
  Modal,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { makeStylesheet } from "~/components/AppShell.styles";
import { AppText, BrandMark, Button, ButtonText, Stack } from "~/components/ui";
import { useAuth, type AuthUser } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { useItem } from "~/state/items";
import { useKit } from "~/state/kits";
import { useSyncStatus } from "~/state/sync-status";
import { useTheme, useThemedStyles } from "~/styles/theme";
import { openExternalUrl } from "~/utils/external-link";
import { getExternalLinks } from "~/utils/runtime-config";
import { SUBSCRIPTION_PATH } from "~/utils/onboarding";

const navItems = [
  { href: "/", label: "Workshop", icon: "layout-dashboard" },
  { href: "/kits", label: "Kits", icon: "boxes" },
  { href: "/items", label: "All items", icon: "wrench" },
] as const;

function parentRoute(pathname: string) {
  if (/^\/(kits|items)\/[^/]+$/.test(pathname)) {
    return pathname.replace(/\/[^/]+$/, "");
  }
  return null;
}

function userInitials(user: AuthUser) {
  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join("");

  return initials || user.email.charAt(0).toUpperCase();
}

function SyncPill() {
  const stylesheet = useThemedStyles(makeStylesheet);
  const status = useSyncStatus();

  // Offline-first: once the inventory is on screen, a persistent "Loading" or
  // "All synced" line is just noise. Only surface the states that need action.
  if (status.state === "synced" || status.state === "loading") {
    return null;
  }

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`${status.label}. ${status.hint}.`}
      style={stylesheet.syncPill}
    >
      <View
        style={[
          stylesheet.syncDot,
          status.state === "offline"
            ? stylesheet.syncDotError
            : stylesheet.syncDotPending,
        ]}
      />
      <Text numberOfLines={1} style={stylesheet.syncLabel}>
        {status.label}
      </Text>
    </View>
  );
}

function Navigation({
  user,
  logoutPending,
  isPro,
  onLogoutRequest,
  onNavigate,
}: {
  user: AuthUser | null;
  logoutPending: boolean;
  isPro: boolean;
  onLogoutRequest: () => void;
  onNavigate?: () => void;
}) {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStylesheet);
  const pathname = usePathname();
  const router = useRouter();
  const externalLinks = getExternalLinks();

  return (
    <View style={stylesheet.sidebarBody}>
      <View style={stylesheet.sidebarBrand}>
        <BrandMark size="md" />
      </View>
      <SyncPill />
      <Stack gap="xs">
        {navItems.map((item) => {
          const active =
            item.href === "/"
              ? pathname === "/"
              : pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Pressable
              key={item.href}
              accessibilityRole="link"
              accessibilityState={{ selected: active }}
              onPress={() => {
                router.push(item.href as never);
                onNavigate?.();
              }}
              style={({ hovered, pressed }) => [
                stylesheet.navItem,
                active ? stylesheet.navItemActive : null,
                hovered || pressed ? stylesheet.navItemHover : null,
              ]}
            >
              <Lucide
                name={item.icon}
                size={17}
                color={active ? colors.primary : colors.mutedForeground}
              />
              <Text
                style={[
                  stylesheet.navItemText,
                  active ? stylesheet.navItemTextActive : null,
                ]}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
        <View style={stylesheet.sidebarDivider} />
        <Pressable
          accessibilityRole="link"
          accessibilityState={{ selected: pathname === "/settings" }}
          onPress={() => {
            router.push("/settings" as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === "/settings" ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <Text
            style={[
              stylesheet.sidebarUtilityText,
              pathname === "/settings"
                ? stylesheet.sidebarUtilityTextActive
                : null,
            ]}
          >
            Settings
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="link"
          accessibilityState={{ selected: pathname === SUBSCRIPTION_PATH }}
          onPress={() => {
            router.push(SUBSCRIPTION_PATH as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === SUBSCRIPTION_PATH ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <View style={stylesheet.utilityContent}>
            <Text
              style={[
                stylesheet.sidebarUtilityText,
                pathname === SUBSCRIPTION_PATH
                  ? stylesheet.sidebarUtilityTextActive
                  : null,
              ]}
            >
              {isPro ? "Subscription" : "Upgrade to Pro"}
            </Text>
            <View style={stylesheet.planBadge}>
              <Text style={stylesheet.planBadgeText}>
                {isPro ? "PRO" : "FREE"}
              </Text>
            </View>
          </View>
        </Pressable>
        {externalLinks.help ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => void openExternalUrl(externalLinks.help)}
            style={({ hovered, pressed }) => [
              stylesheet.sidebarUtilityLink,
              hovered || pressed ? stylesheet.navItemHover : null,
            ]}
          >
            <Text style={stylesheet.sidebarUtilityText}>Help</Text>
          </Pressable>
        ) : null}
        {user ? (
          <Button
            variant="link"
            align="start"
            iconName="log-out"
            onPress={onLogoutRequest}
            disabled={logoutPending}
          >
            <ButtonText>
              {logoutPending ? "Signing out..." : "Sign out"}
            </ButtonText>
          </Button>
        ) : (
          <Button
            variant="link"
            align="start"
            iconName="user-plus"
            onPress={() => {
              router.push("/register" as never);
              onNavigate?.();
            }}
          >
            <ButtonText>Create free account</ButtonText>
          </Button>
        )}
      </Stack>

      <View style={{ flex: 1 }} />
      <View style={stylesheet.sidebarFooter}>
        {user ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Open profile settings"
            onPress={() => {
              router.push("/settings" as never);
              onNavigate?.();
            }}
            style={({ hovered, pressed }) => [
              stylesheet.sidebarProfile,
              hovered || pressed ? stylesheet.navItemHover : null,
            ]}
          >
            <View style={stylesheet.sidebarAvatar}>
              <Text style={stylesheet.sidebarAvatarText}>
                {userInitials(user)}
              </Text>
            </View>
            <Text numberOfLines={1} style={stylesheet.sidebarProfileName}>
              {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                user.email}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function titleCase(value: string) {
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const SECTION_LABELS: Record<string, string> = {
  "": "Workshop",
  kits: "Kits",
  items: "All items",
  settings: "Settings",
  subscription: "Subscription",
  support: "Support",
};

/** `/kits/:id` and `/items/:id` — pull out the id so the shell can name it. */
function detailRoute(pathname: string) {
  const match = /^\/(kits|items)\/([^/]+)$/.exec(pathname);
  if (!match) return null;
  return { section: match[1] as string, id: match[2] as string };
}

function sectionLabel(segment: string) {
  return SECTION_LABELS[segment] ?? (titleCase(segment) || "Workshop");
}

function breadcrumbsFor(pathname: string, detailLabel?: string) {
  if (/^\/support\/[^/]+$/.test(pathname)) {
    return [{ label: "Support" }, { label: "Ticket" }];
  }
  const detail = detailRoute(pathname);
  if (detail) {
    return [
      { label: sectionLabel(detail.section) },
      { label: detailLabel ?? "…" },
    ];
  }
  const segment = pathname.split("/").filter(Boolean).at(-1) ?? "";
  return [{ label: sectionLabel(segment) }];
}

function pageTitleFor(pathname: string, detailLabel?: string) {
  const crumbs = breadcrumbsFor(pathname, detailLabel);
  return `${crumbs.at(-1)?.label ?? "GearSync"} | GearSync`;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStylesheet);
  const pathname = usePathname();
  const router = useRouter();
  const { user, logout, logoutPending } = useAuth();
  const plan = usePlan();
  const desktop = width >= 768;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const backRoute = Platform.OS === "web" ? null : parentRoute(pathname);
  const detail = detailRoute(pathname);
  const detailItem = useItem(
    detail?.section === "items" ? detail.id : undefined,
  );
  const detailKit = useKit(detail?.section === "kits" ? detail.id : undefined);
  const detailLabel = detailItem?.name ?? detailKit?.name ?? undefined;
  const breadcrumbs = breadcrumbsFor(pathname, detailLabel);
  const nearingItemLimit =
    !plan.isPro &&
    plan.items.remaining !== null &&
    plan.items.remaining <= 2 &&
    pathname !== SUBSCRIPTION_PATH;

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    document.title = pageTitleFor(pathname, detailLabel);
  }, [pathname, detailLabel]);

  return (
    <View style={stylesheet.appShell}>
      {desktop ? (
        <View style={stylesheet.sidebar}>
          <Navigation
            user={user}
            logoutPending={logoutPending}
            isPro={plan.isPro}
            onLogoutRequest={() => setLogoutConfirmOpen(true)}
          />
        </View>
      ) : null}

      <View style={stylesheet.appShellContent}>
        {nearingItemLimit ? (
          <View style={stylesheet.upgradeBanner}>
            <AppText size="xs">
              {plan.items.remaining === 0
                ? "You've used all 10 free items. Upgrade to keep cataloguing."
                : `${plan.items.remaining} free item ${plan.items.remaining === 1 ? "slot" : "slots"} left.`}
            </AppText>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel="See GearSync Pro"
              onPress={() => router.push(SUBSCRIPTION_PATH as never)}
            >
              <AppText size="xs" weight="semibold">
                See Pro →
              </AppText>
            </Pressable>
          </View>
        ) : null}
        <View style={stylesheet.shellHeader}>
          <View style={stylesheet.shellHeaderLeft}>
            {!desktop ? (
              <Button
                variant="outline"
                compact
                onPress={() => setDrawerOpen(true)}
              >
                <ButtonText>Menu</ButtonText>
              </Button>
            ) : null}
            {backRoute ? (
              <Button
                variant="outline"
                compact
                onPress={() => router.replace(backRoute as never)}
              >
                <ButtonText>Back</ButtonText>
              </Button>
            ) : null}
            {!desktop ? <BrandMark size="sm" /> : null}
            <View style={stylesheet.breadcrumbs}>
              {(desktop ? breadcrumbs : breadcrumbs.slice(-1)).map(
                (breadcrumb, index, shown) => (
                  <View
                    key={`${breadcrumb.label}-${index}`}
                    style={stylesheet.breadcrumbItem}
                  >
                    {index > 0 ? (
                      <Text style={stylesheet.breadcrumbSeparator}>
                        {" / "}
                      </Text>
                    ) : null}
                    <Text
                      numberOfLines={1}
                      style={[
                        stylesheet.breadcrumbCrumb,
                        index === shown.length - 1
                          ? stylesheet.breadcrumbCurrent
                          : null,
                      ]}
                    >
                      {breadcrumb.label}
                    </Text>
                  </View>
                ),
              )}
            </View>
          </View>
          <View style={stylesheet.shellActions}>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel="Search items"
              onPress={() => router.push("/items" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.headerIconButton,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Lucide name="search" size={18} color={colors.heading} />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Support"
              onPress={() => router.push("/support" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.headerIconButton,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Lucide name="megaphone" size={18} color={colors.heading} />
            </Pressable>
          </View>
        </View>
        {children}
      </View>

      <Modal
        animationType="slide"
        transparent
        visible={drawerOpen}
        onRequestClose={() => setDrawerOpen(false)}
      >
        <View style={stylesheet.drawerOverlay}>
          <View style={stylesheet.drawerPanel}>
            <Navigation
              user={user}
              logoutPending={logoutPending}
              isPro={plan.isPro}
              onLogoutRequest={() => {
                setDrawerOpen(false);
                setLogoutConfirmOpen(true);
              }}
              onNavigate={() => setDrawerOpen(false)}
            />
          </View>
          <Pressable
            accessibilityLabel="Close navigation"
            style={stylesheet.drawerDismiss}
            onPress={() => setDrawerOpen(false)}
          />
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={logoutConfirmOpen}
        onRequestClose={() => setLogoutConfirmOpen(false)}
      >
        <View style={stylesheet.modalOverlay}>
          <View style={stylesheet.confirmationCard}>
            <View>
              <Text style={stylesheet.modalTitle}>Sign out?</Text>
              <AppText>
                Gear saved on this device stays here until you sign back in.
              </AppText>
            </View>
            <View style={stylesheet.confirmationActions}>
              <Button
                variant="outline"
                compact
                onPress={() => setLogoutConfirmOpen(false)}
                disabled={logoutPending}
              >
                <ButtonText>Cancel</ButtonText>
              </Button>
              <Button
                compact
                onPress={() => void logout()}
                disabled={logoutPending}
              >
                <ButtonText>
                  {logoutPending ? "Signing out..." : "Sign out"}
                </ButtonText>
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
